import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ClipboardCheck, Brain, Users, Award, Target, CheckCircle2, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import LeadForm from '@/components/shared/LeadForm';

const SERVICES = [
  {
    id: 'psychometric-tools',
    icon: Brain,
    title: 'Psychometric Assessment Tools',
    tagline: 'Scientific tools for selection, training & development',
    description: 'Comprehensive suite of validated assessment tools measuring ability, personality, and role-specific competencies for informed talent decisions across the employee lifecycle.',
    tools: [
      { category: 'Ability Assessments', name: 'Internet Reasoning Test (IRT)', detail: 'Measures verbal, numerical, and abstract reasoning. Suitable for remote supervision' },
      { category: 'Ability Assessments', name: 'Adaptive General Reasoning (AdaptGRT)', detail: 'Item-based assessment useful for training needs, promotion potential, and minimum ability requirements' },
      { category: 'Ability Assessments', name: 'Critical Reasoning Test Battery (CRTB)', detail: 'Assesses numerical and verbal reasoning reflecting current levels and real-world problem solving' },
      { category: 'Personality', name: '15 Factor Questionnaire Plus (15FQ+)', detail: 'Trait-based assessment measuring 16 personality factors across interpersonal, thinking, and coping styles' },
      { category: 'Sales Roles', name: 'General Sales Selection Report', detail: 'Assesses sales process and culture fit, provides targeted interview probes' },
      { category: 'Sales Roles', name: 'General Sales Development Report', detail: 'Measures personality and motivational characteristics, identifies best sales culture fit' },
      { category: 'Multi-Rater', name: '360 Degree Feedback Report', detail: 'Customized based on organizational competencies, provides anonymous insights from multiple perspectives' }
    ],
    approach: [
      { step: 'Assessment Selection', detail: 'Identify appropriate tools based on role requirements and organizational context' },
      { step: 'Customization', detail: 'Tailor assessments to measure competencies critical to your success criteria' },
      { step: 'Administration', detail: 'Deploy assessments through secure online platform with candidate support' },
      { step: 'Scoring & Analysis', detail: 'Generate detailed reports with normative comparisons and interpretations' },
      { step: 'Feedback & Integration', detail: 'Provide candidate feedback and integrate results into talent decisions' }
    ],
    deliverables: ['Assessment reports with interpretations', 'Normative benchmarking', 'Interview guides', 'Development recommendations', 'Aggregated analytics'],
    outcomes: 'Accurate talent assessment, reduced hiring errors, personalized development plans, objective evaluations',
    caseStudy: { client: 'Multiple Organizations', result: 'Improved quality of hire, better role fit, 30% reduction in early attrition' }
  },
  {
    id: 'adc',
    icon: ClipboardCheck,
    title: 'Assessment & Development Centres (ADC)',
    tagline: 'Scientific approach to building future-ready workforce',
    description: 'Customized assessment centers at all organizational levels delivering valuable insights through multiple evaluation methods and competency-based assessments for selection, development, and succession planning.',
    approach: [
      { step: 'Customize Assessment Tools', detail: 'Identify critical competencies and select or customize tools aligned with strategic goals' },
      { step: 'Design Assessment Center', detail: 'Create multi-method assessment design including CBI, role plays, case studies, group discussions' },
      { step: 'Schedule & Communicate', detail: 'Plan logistics, brief participants, and ensure assessor readiness' },
      { step: 'Conduct Assessment Centre', detail: 'Execute multi-day assessment with trained assessors observing and rating behaviors' },
      { step: 'Evaluate & Calibrate', detail: 'Assessors discuss observations, rate competencies, and reach consensus on ratings' },
      { step: 'Feedback & Reporting', detail: 'Provide detailed feedback on performance, strengths, and developmental needs to participants and sponsors' },
      { step: 'Development Planning', detail: 'Create Individual Development Plans to bridge competency gaps and accelerate growth' }
    ],
    deliverables: ['Customized assessment center design', 'Multi-method evaluation exercises', 'Detailed assessment reports', 'Individual Development Plans', 'Talent pool analysis', 'Succession readiness insights'],
    outcomes: 'Data-driven talent decisions, identified high potentials, development roadmaps, succession pipeline, reduced promotion failures',
    caseStudy: { client: 'Banking Organization', result: '1,175 leaders assessed across 4 job scales, 100% received IDPs, improved succession readiness' }
  },
  {
    id: 'competency-framework',
    icon: Target,
    title: 'Competency Framework Development',
    tagline: 'Blueprint for organizational behavior and talent management',
    description: 'Create structured, comprehensive collections of skills, behaviors, and knowledge required for effective performance. Integrate competency frameworks across all talent management initiatives for consistency and strategic alignment.',
    approach: [
      { step: 'Competency Identification', detail: 'Define core, managerial, and leadership competencies aligned with business strategy through job analysis and strategic workshops' },
      { step: 'Behavioral Indicators', detail: 'Articulate observable behaviors for each competency at different proficiency levels' },
      { step: 'Validation', detail: 'Validate competencies through structured interviews, focus groups, and alignment with business outcomes' },
      { step: 'Role Mapping', detail: 'Map competencies to specific roles, job families, and career levels creating competency profiles' },
      { step: 'Integration Planning', detail: 'Design integration into recruitment, performance management, learning, succession planning, and compensation' },
      { step: 'Communication & Rollout', detail: 'Train stakeholders and communicate framework across the organization' }
    ],
    applications: [
      { area: 'Recruitment & Selection', benefit: 'Integrate competency-based interviewing, eliminate bias, improve quality of hire' },
      { area: 'Performance Management', benefit: 'Set clear performance standards, identify development needs, link performance to competencies' },
      { area: 'Succession Planning', benefit: 'Identify readiness for critical positions, create targeted development plans' },
      { area: 'Learning & Development', benefit: 'Design targeted training programs, prioritize development investments' },
      { area: 'Onboarding', benefit: 'Accelerate new hire productivity with competency-based orientation' },
      { area: 'Compensation', detail: 'Link pay to demonstrated competencies and skill acquisition' },
      { area: 'Organizational Alignment', benefit: 'Ensure behaviors align with strategy and values' },
      { area: 'Career Pathing', benefit: 'Provide clear competency roadmaps for career progression' }
    ],
    deliverables: ['Competency dictionary with behavioral indicators', 'Role-based competency profiles', 'Integration toolkit', 'Training materials', 'Assessment criteria'],
    outcomes: 'Aligned behavior with strategy, clear performance standards, integrated talent processes, common language for talent',
    caseStudy: { client: 'Multiple Organizations', result: 'Seamless integration across talent lifecycle, improved performance clarity, reduced subjectivity' }
  },
  {
    id: 'certifications',
    icon: Award,
    title: 'Professional Certifications',
    tagline: 'Build in-house assessment expertise',
    description: 'Specialized certification programs to build internal assessment capability on industry-standard tools and methodologies, reducing dependence on external consultants and building sustainable talent practices.',
    programs: [
      { 
        title: '15FQ+ Certification Program',
        duration: '3 days',
        detail: 'Become a certified interpreter of the 15FQ+ personality assessment. Gain practical knowledge on administering, scoring, interpreting, and providing feedback to candidates. Learn to integrate personality insights into selection and development decisions.',
        outcomes: ['Certified 15FQ+ interpreter', 'Ability to administer and score assessments', 'Skills to provide candidate feedback', 'Integration into selection processes']
      },
      { 
        title: 'Assessment & Development Centre Assessors Certification',
        duration: '4 days',
        detail: 'Comprehensive training on designing and running effective Assessment and Development Centers. Learn observation techniques, rating accuracy, exercise design (inbox, role plays, group discussions), and assessor calibration processes.',
        outcomes: ['Design effective ADCs', 'Accurate behavioral observation', 'Competency-based rating skills', 'Assessor calibration expertise']
      },
      { 
        title: 'Competency Based Interview (CBI) Certification',
        duration: '2 days',
        detail: 'Master the art and science of competency-based interviewing. Learn to create behavioral questions using the STAR/STARR technique, apply the ORCE methodology (Observe, Record, Classify, Evaluate), and conduct structured, objective interviews that predict performance.',
        outcomes: ['Structured interview skills', 'Behavioral question design', 'STARR technique mastery', 'Objective evaluation capability']
      }
    ],
    deliverables: ['Certification credentials', 'Assessment tools and licenses', 'Reference materials and job aids', 'Practice exercises', 'Post-training support'],
    outcomes: 'Internal assessment capability, reduced external costs, consistent evaluation standards, sustainable talent practices',
    caseStudy: { client: 'Multiple Organizations', result: '20-30% reduction in external assessment costs, improved hiring manager effectiveness, standardized talent decisions' }
  }
];

export default function AssessmentsPage() {
  const [expandedService, setExpandedService] = useState(null);

  return (
    <div className="bg-[#FAF5EF]">
      {/* Hero */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-br from-[#1A1008] to-[#3D1C11] text-white">
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute top-20 right-20 w-96 h-96 bg-[#E8642C] rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <div className="max-w-[900px]">
            <p className="text-[11px] font-semibold text-[#E8642C] tracking-[0.25em] uppercase mb-6">Assessments & Development</p>
            <h1 className="text-[clamp(3rem,6vw,5.5rem)] font-bold leading-[1.05] tracking-[-0.02em] mb-8 text-white">
              Discover the science and art in behavior.
            </h1>
            <p className="text-xl text-white/80 leading-relaxed mb-12">
              Assess and develop your people to transform your talent strategy with proven psychometric tools, competency frameworks, and Assessment & Development Centers.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
              {[
                { value: '1,175', label: 'Leaders Assessed', sublabel: 'Banking ADC (12 months)' },
                { value: '100%', label: 'Received IDPs', sublabel: 'Development Plans' },
                { value: '7', label: 'Assessment Tools', sublabel: 'Validated & Certified' },
                { value: '50+', label: 'Competencies', sublabel: 'Mapped & Assessed' }
              ].map((stat, i) => (
                <div key={i}>
                  <div className="text-3xl lg:text-4xl font-bold text-white mb-2">{stat.value}</div>
                  <div className="text-xs text-white/60 uppercase tracking-wide mb-1">{stat.label}</div>
                  <div className="text-xs text-white/40">{stat.sublabel}</div>
                </div>
              ))}
            </div>
            
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#E8642C] text-white px-8 py-4 rounded-full text-sm font-semibold hover:bg-[#CC2929] transition-all shadow-lg">
                Explore Solutions
                <ArrowRight size={16} />
              </Link>
              <Link to="/impact" className="inline-flex items-center gap-2 bg-white/10 text-white px-8 py-4 rounded-full text-sm font-semibold hover:bg-white/20 transition-all border border-white/20">
                View Results
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1A1008] mb-6">Assessment Services</h2>
            <p className="text-lg text-[#6B5B50] max-w-[700px] mx-auto">
              Four comprehensive assessment solutions. Click to explore each in detail.
            </p>
          </div>

          <div className="space-y-6">
            {SERVICES.map((service, i) => (
              <DetailedServiceCard 
                key={service.id} 
                service={service} 
                index={i}
                isExpanded={expandedService === service.id}
                onToggle={() => setExpandedService(expandedService === service.id ? null : service.id)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#FAF5EF]">
        <div className="mx-auto w-full max-w-[1000px] px-6 sm:px-8 lg:px-12">
          <div className="bg-white rounded-3xl p-10 lg:p-16 shadow-xl">
            <div className="text-center mb-10">
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1A1008] mb-6">Transform your talent strategy</h2>
              <p className="text-lg text-[#6B5B50] max-w-[600px] mx-auto">Let's discuss how our assessment solutions can build your future-ready workforce.</p>
            </div>
            <LeadForm source="assessments_page" />
          </div>
        </div>
      </section>
    </div>
  );
}

function DetailedServiceCard({ service, index, isExpanded, onToggle }) {
  const { ref, inView } = useInView({ threshold: 0.15, triggerOnce: true });
  const Icon = service.icon;
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
      className="bg-white rounded-3xl border border-[#3D1C11]/10 hover:border-[#E8642C]/30 transition-all overflow-hidden"
    >
      <button
        onClick={onToggle}
        className="w-full p-8 lg:p-10 flex items-start gap-6 text-left hover:bg-[#FAF5EF]/50 transition-colors"
      >
        <div className="flex-shrink-0 w-14 h-14 bg-[#E8642C]/10 rounded-2xl flex items-center justify-center">
          <Icon className="w-7 h-7 text-[#E8642C]" />
        </div>
        
        <div className="flex-1">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-2xl font-bold text-[#1A1008] mb-2">{service.title}</h3>
              <p className="text-sm text-[#E8642C] font-medium mb-3">{service.tagline}</p>
              <p className="text-[#6B5B50] leading-relaxed">{service.description}</p>
            </div>
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.3 }}
              className="flex-shrink-0"
            >
              <ChevronDown className="w-6 h-6 text-[#6B5B50]" />
            </motion.div>
          </div>
        </div>
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="px-8 lg:px-10 pb-10 pt-4 border-t border-[#3D1C11]/10 bg-[#FAF5EF]/30">
              {/* Tools (if present) */}
              {service.tools && (
                <div className="mb-8">
                  <h4 className="text-lg font-bold text-[#1A1008] mb-6 flex items-center gap-2">
                    <div className="w-1 h-5 bg-[#E8642C] rounded-full" />
                    Assessment Tools
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    {service.tools.map((tool, i) => (
                      <div key={i} className="bg-white rounded-xl p-4 border border-[#3D1C11]/10">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="font-bold text-[#1A1008] text-sm">{tool.name}</div>
                          <div className="text-xs text-[#E8642C] font-medium px-2 py-1 bg-[#E8642C]/10 rounded whitespace-nowrap">{tool.category}</div>
                        </div>
                        <div className="text-xs text-[#6B5B50] leading-relaxed">{tool.detail}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Programs (if present) */}
              {service.programs && (
                <div className="mb-8">
                  <h4 className="text-lg font-bold text-[#1A1008] mb-6 flex items-center gap-2">
                    <div className="w-1 h-5 bg-[#E8642C] rounded-full" />
                    Certification Programs
                  </h4>
                  <div className="space-y-4">
                    {service.programs.map((program, i) => (
                      <div key={i} className="bg-white rounded-xl p-5 border border-[#3D1C11]/10">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div className="font-bold text-[#1A1008]">{program.title}</div>
                          <div className="text-xs text-[#E8642C] font-medium px-2 py-1 bg-[#E8642C]/10 rounded whitespace-nowrap">{program.duration}</div>
                        </div>
                        <div className="text-xs text-[#6B5B50] mb-3 leading-relaxed">{program.detail}</div>
                        <div className="flex flex-wrap gap-2">
                          {program.outcomes.map((outcome, j) => (
                            <div key={j} className="text-xs px-3 py-1 bg-[#FAF5EF] text-[#6B5B50] rounded-full border border-[#3D1C11]/10">{outcome}</div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Applications (if present) */}
              {service.applications && (
                <div className="mb-8">
                  <h4 className="text-lg font-bold text-[#1A1008] mb-6 flex items-center gap-2">
                    <div className="w-1 h-5 bg-[#E8642C] rounded-full" />
                    Applications Across Talent Lifecycle
                  </h4>
                  <div className="grid md:grid-cols-2 gap-3">
                    {service.applications.map((app, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#E8642C] flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-sm font-bold text-[#1A1008]">{app.area}</div>
                          <div className="text-xs text-[#6B5B50]">{app.benefit}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid lg:grid-cols-2 gap-10">
                {/* Approach (if present) */}
                {service.approach && (
                  <div>
                    <h4 className="text-lg font-bold text-[#1A1008] mb-6 flex items-center gap-2">
                      <div className="w-1 h-5 bg-[#E8642C] rounded-full" />
                      Our Approach
                    </h4>
                    <div className="space-y-4">
                      {service.approach.map((step, i) => (
                        <div key={i} className="flex gap-3">
                          <div className="flex-shrink-0 w-8 h-8 bg-[#E8642C]/10 rounded-lg flex items-center justify-center text-[#E8642C] font-bold text-sm">
                            {i + 1}
                          </div>
                          <div>
                            <div className="font-bold text-[#1A1008] text-sm mb-1">{step.step}</div>
                            <div className="text-xs text-[#6B5B50] leading-relaxed">{step.detail}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Deliverables & Outcomes */}
                <div>
                  <h4 className="text-lg font-bold text-[#1A1008] mb-6 flex items-center gap-2">
                    <div className="w-1 h-5 bg-[#E8642C] rounded-full" />
                    Deliverables
                  </h4>
                  <div className="space-y-2 mb-8">
                    {service.deliverables.map((item, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#E8642C] flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-[#6B5B50]">{item}</span>
                      </div>
                    ))}
                  </div>

                  <h4 className="text-lg font-bold text-[#1A1008] mb-4 flex items-center gap-2">
                    <div className="w-1 h-5 bg-[#E8642C] rounded-full" />
                    Expected Outcomes
                  </h4>
                  <p className="text-sm text-[#E8642C] font-medium mb-6">{service.outcomes}</p>

                  {service.caseStudy && (
                    <div className="bg-white rounded-xl p-5 border border-[#E8642C]/20">
                      <div className="text-xs font-bold text-[#1A1008] uppercase tracking-wide mb-2">Case Study</div>
                      <div className="text-sm text-[#6B5B50]">
                        <span className="font-semibold text-[#1A1008]">{service.caseStudy.client}:</span> {service.caseStudy.result}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
